const DB_NAME = "pos-local-db";
const STORE_NAME = "fnb-settings";
const DB_VERSION = 2;
const WORKSTATION_KEY = "workstation";

export const WORKSTATION_UPDATED_EVENT = "workstation-updated";

export const WORKSTATION_TYPES = {
  BO: "BO",
  FNB: "FNB",
  BOFNB: "BOFNB",
  GAMES: "GAMES",
  BOFNBGAMES: "BOFNBGAMES",
};

let dbInstance = null;

function openDb() {
  return new Promise((resolve, reject) => {
    if (dbInstance) return resolve(dbInstance);

    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser."));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = event => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getWorkstation() {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(WORKSTATION_KEY);

    request.onsuccess = event => resolve(event.target.result || "");
    request.onerror = () => reject(request.error);
  });
}

/**
 * Which header nav tabs this workstation may show.
 * Empty/unknown → all tabs (legacy / unset).
 */
export function getWorkstationNavFlags(workstationType = "") {
  const type = String(workstationType || "").trim().toUpperCase();

  if (type === WORKSTATION_TYPES.BO) {
    return { showSchedules: true, showFnb: false, showGames: false };
  }
  if (type === WORKSTATION_TYPES.FNB) {
    return { showSchedules: false, showFnb: true, showGames: false };
  }
  if (type === WORKSTATION_TYPES.GAMES) {
    return { showSchedules: false, showFnb: false, showGames: true };
  }
  if (type === WORKSTATION_TYPES.BOFNB) {
    return { showSchedules: true, showFnb: true, showGames: false };
  }
  if (type === WORKSTATION_TYPES.BOFNBGAMES) {
    return { showSchedules: true, showFnb: true, showGames: true };
  }

  // Unset / unknown → do not restrict
  return { showSchedules: true, showFnb: true, showGames: true };
}

/** Preferred landing route after applying a workstation type. */
export function getWorkstationHomePath(workstationType = "") {
  const type = String(workstationType || "").trim().toUpperCase();
  if (type === WORKSTATION_TYPES.FNB) return "/pos-fnb";
  if (type === WORKSTATION_TYPES.GAMES) return "/pos-games";
  if (
    type === WORKSTATION_TYPES.BO ||
    type === WORKSTATION_TYPES.BOFNB ||
    type === WORKSTATION_TYPES.BOFNBGAMES
  ) {
    return "/pos-booking";
  }
  return null;
}

export async function saveWorkstation(workstation) {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    store.put(workstation, WORKSTATION_KEY);

    tx.oncomplete = () => {
      window.dispatchEvent(
        new CustomEvent(WORKSTATION_UPDATED_EVENT, {
          detail: workstation
        })
      );
      resolve(true);
    };
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function clearWorkstation() {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    store.delete(WORKSTATION_KEY);

    tx.oncomplete = () => {
      window.dispatchEvent(
        new CustomEvent(WORKSTATION_UPDATED_EVENT, {
          detail: ""
        })
      );
      resolve(true);
    };
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/* ------------------------------------------------------------------ hardware name */

const HARDWARE_NAME_KEY = "hardware_name";

/**
 * The terminal's own name. It lives in IndexedDB rather than localStorage because logout
 * wipes localStorage, and a terminal keeps its identity across logins.
 *
 * Most callers build their payload inline and cannot await, so the value is cached in memory
 * at boot by initHardwareName() and read back through getHardwareNameSync().
 */
let hardwareNameCache = "";
let hardwareNameClearPatched = false;

/**
 * Logout calls localStorage.clear() in a dozen places. Keep the mirrored copy of the hardware
 * name through it, the same way the print settings are preserved.
 */
export function patchLocalStorageClearToPreserveHardwareName() {
  if (
    hardwareNameClearPatched ||
    typeof window === "undefined" ||
    !window.localStorage
  ) {
    return;
  }
  hardwareNameClearPatched = true;

  const originalClear = window.localStorage.clear.bind(window.localStorage);
  window.localStorage.clear = function() {
    let backup = null;
    try {
      backup = window.localStorage.getItem(HARDWARE_NAME_KEY);
    } catch (e) {}
    originalClear();
    if (backup) {
      try {
        window.localStorage.setItem(HARDWARE_NAME_KEY, backup);
      } catch (e) {}
    }
  };
}

/** IndexedDB is the source of truth, localStorage is the mirror every payload already reads. */
function mirrorHardwareName(value) {
  hardwareNameCache = value;
  try {
    window.localStorage.setItem(HARDWARE_NAME_KEY, value);
  } catch (e) {}
  return value;
}

function generateHardwareName() {
  const random = Math.random()
    .toString(36)
    .slice(2, 8)
    .toUpperCase();
  return `POS-${random}`;
}

export function getHardwareNameSync() {
  return hardwareNameCache;
}

export async function saveHardwareName(name) {
  const value = String(name || "").trim() || generateHardwareName();
  const db = await openDb();

  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, HARDWARE_NAME_KEY);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

  return mirrorHardwareName(value);
}

/** Reads the stored name, minting and persisting a POS-XXXXXX one the first time. */
export async function getHardwareName() {
  const db = await openDb();

  const stored = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(HARDWARE_NAME_KEY);
    request.onsuccess = event => resolve(event.target.result || "");
    request.onerror = () => reject(request.error);
  });

  if (stored) {
    return mirrorHardwareName(stored);
  }

  return saveHardwareName(generateHardwareName());
}

/** Call once at start up so the synchronous accessor has something to return. */
export async function initHardwareName() {
  patchLocalStorageClearToPreserveHardwareName();
  try {
    return await getHardwareName();
  } catch (error) {
    console.log("Unable to load hardware name from IndexedDB", error);
    return hardwareNameCache;
  }
}



// WEBPACK FOOTER //
// ./src/util/indexDb/workstationIndexDb.js